type ApiErrorOptions = {
  defaultMessage: string;
  endpointHints?: string[];
};

const MIGRATION_HINT_MESSAGE =
  'Possível incompatibilidade de schema no backend (migrations pendentes). ' +
  'Solicite execução de "python check_db_sync.py" na pasta backend e reinício da API.';

/** Mapeia nomes do body (FastAPI/Pydantic) para rótulos em português na área de compras */
const PURCHASE_BODY_FIELD_LABELS: Record<string, string> = {
  object: 'Objeto',
  items_description: 'Descrição dos Itens',
  title: 'Título',
  description: 'Descrição',
  workflow_id: 'Fluxo',
  assigned_group_id: 'Grupo',
};

/** Rótulos para campos de body em rotas comuns (oportunidades, documentos, etc.) */
const COMMON_BODY_FIELD_LABELS: Record<string, string> = {
  ...PURCHASE_BODY_FIELD_LABELS,
  name: 'Nome',
  filename: 'Nome do arquivo',
  parent_folder_id: 'Pasta superior',
  folder_id: 'Pasta',
  document_ids: 'Documentos',
  file: 'Arquivo',
  status: 'Estado',
  camud_go_no_go_date: 'Agendamento CAMUD Go/No-Go',
  risk_committee_start_at: 'Agendamento Riscos e Impactos',
  risk_committee_end_at: 'Agendamento Riscos e Impactos',
};

/** Mensagens frequentes do Pydantic / Starlette em inglês → PT-BR */
const PYDANTIC_MSG_EN_TO_PT: Record<string, string> = {
  'field required': 'Campo obrigatório',
  'none is not an allowed value': 'É necessário informar um valor.',
  'input should be a valid date': 'Informe uma data válida.',
  'value is not a valid integer': 'O valor não é um número inteiro válido.',
  'value is not a valid float': 'O valor não é um número decimal válido.',
  'value is not a valid boolean': 'O valor não é verdadeiro/falso válido.',
  'value is not a valid list': 'O valor não é uma lista válida.',
  'value is not a valid dict': 'O valor não é um objeto JSON válido.',
  'string too short': 'Texto curto demais.',
  'string too long': 'Texto longo demais.',
  'unexpected field': 'Campo não permitido',
  'invalid json': 'JSON inválido',
};

/**
 * Traduz mensagens de erro da API que ainda possam vir em inglês (cache antigo, proxy, Pydantic genérico).
 * Usado na página de oportunidades e no restante da app ao formatar `detail`.
 */
export const translateKnownEnglishApiDetail = (input: string): string => {
  const raw = (input || '').trim();
  if (!raw) return input;

  if (raw.includes('\n')) {
    return raw
      .split(/\n+/)
      .map((segment) => translateKnownEnglishApiDetail(segment.trim()))
      .filter((line) => line.length > 0)
      .join('\n');
  }

  const exactLower = new Map<string, string>([
    ['file too large.', 'O arquivo excede o tamanho máximo permitido (100 MB).'],
    ['file too large', 'O arquivo excede o tamanho máximo permitido (100 MB).'],
    ['file type not allowed.', 'Tipo de arquivo não permitido.'],
    ['file type not allowed', 'Tipo de arquivo não permitido.'],
    ['opportunity not found.', 'Oportunidade não encontrada.'],
    ['opportunity not found', 'Oportunidade não encontrada.'],
    ['folder not found.', 'Pasta não encontrada.'],
    ['folder not found', 'Pasta não encontrada.'],
    ['document not found.', 'Documento não encontrado.'],
    ['document not found', 'Documento não encontrado.'],
    ['not enough permissions.', 'Sem permissão suficiente.'],
    ['not enough permissions', 'Sem permissão suficiente.'],
    ['access denied', 'Acesso negado.'],
    ['internal server error', 'Erro interno no servidor. Tente novamente ou contacte o suporte.'],
    ['bad request', 'Requisição inválida.'],
    ['not found', 'Não encontrado.'],
    ['forbidden', 'Acesso proibido.'],
    ['unauthorized', 'Não autenticado. Faça login novamente.'],
    [
      'network error',
      'Não foi possível contactar a API. Verifique sua conexão; em desenvolvimento, confira também o terminal do backend.',
    ],
    ['failed to fetch', 'Não foi possível contactar o servidor. Verifique a internet.'],
    ['load failed', 'Falha ao carregar dados.'],
    ['request entity too large', 'O arquivo enviado é grande demais para o servidor.'],
    ['payload too large', 'O arquivo enviado é grande demais para o servidor.'],
    ['folder does not belong to this opportunity.', 'A pasta não pertence a esta oportunidade.'],
    ['parent folder does not belong to this opportunity.', 'A pasta superior não pertence a esta oportunidade.'],
    ['target folder does not belong to this opportunity.', 'A pasta de destino não pertence a esta oportunidade.'],
    [
      'target parent folder does not belong to this opportunity.',
      'A pasta de destino (pai) não pertence a esta oportunidade.',
    ],
    ['filename cannot be empty.', 'O nome do arquivo não pode ficar vazio.'],
    ['filename contains invalid characters.', 'O nome do arquivo contém caracteres inválidos.'],
    [
      'one or more documents are invalid for this opportunity.',
      'Um ou mais documentos são inválidos para esta oportunidade.',
    ],
    ['opportunity title cannot be empty.', 'O título da oportunidade não pode ficar vazio.'],
    ['custom field value not found', 'Valor do campo personalizado não encontrado.'],
  ]);

  const key = raw.toLowerCase();
  if (exactLower.has(key)) {
    return exactLower.get(key)!;
  }

  const lower = key;
  if (lower.includes('request failed with status code')) {
    const m = raw.match(/status code\s*(\d{3})/i);
    return m?.[1]
      ? `A requisição falhou (código HTTP ${m[1]}).`
      : 'A requisição falhou.';
  }
  if (lower.includes('timeout') && lower.includes('exceeded')) {
    return 'Tempo esgotado ao contactar o servidor. Tente novamente.';
  }

  return input;
};

const translatePydanticMsg = (msg: string): string => {
  const t = (msg || '').trim();
  if (!t) return msg;
  const k = t.toLowerCase();
  if (PYDANTIC_MSG_EN_TO_PT[k]) return PYDANTIC_MSG_EN_TO_PT[k];
  return msg;
};

/**
 * Formata lista de erros de validação (422) em texto legível, listando campos obrigatórios em português quando possível.
 */
export const formatFastApiValidationErrors = (detail: unknown): string => {
  if (!Array.isArray(detail)) {
    if (detail === null || detail === undefined) {
      return '';
    }
    if (typeof detail === 'object') {
      return JSON.stringify(detail);
    }
    return String(detail);
  }

  const missingLabels: string[] = [];
  const otherLines: string[] = [];

  for (const d of detail as { loc?: string[]; msg?: string }[]) {
    const msg = String(d?.msg ?? '');
    const loc = Array.isArray(d?.loc) ? d.loc! : [];
    const fieldKey = loc.length > 0 ? loc[loc.length - 1] : '';
    const isBodyField = loc.includes('body') && !!fieldKey;
    if (isBodyField && msg.trim().toLowerCase() === 'field required') {
      const label = COMMON_BODY_FIELD_LABELS[fieldKey] ?? fieldKey;
      missingLabels.push(label);
    } else {
      const label = isBodyField
        ? COMMON_BODY_FIELD_LABELS[String(fieldKey)] ?? String(fieldKey)
        : loc.length
          ? loc.join('.')
          : '?';
      otherLines.push(`${label}: ${translatePydanticMsg(msg)}`);
    }
  }

  const uniqueMissing = missingLabels.filter(
    (label, index) => missingLabels.indexOf(label) === index
  );
  let summary = '';
  if (uniqueMissing.length === 1) {
    summary = `O campo ${uniqueMissing[0]} é obrigatório.`;
  } else if (uniqueMissing.length > 1) {
    summary = `Os campos ${uniqueMissing.join(', ')} são obrigatórios.`;
  }

  const fallback = (detail as { loc?: string[]; msg?: string }[])
    .map((e) => `${e.loc?.join('.') ?? ''}: ${translatePydanticMsg(e.msg ?? '')}`)
    .join('\n');

  const parts = [summary, ...otherLines].filter((p) => p.length > 0);
  const joined = parts.length > 0 ? parts.join('\n') : fallback;
  return translateKnownEnglishApiDetail(joined);
};

/**
 * Normaliza o campo `detail` de respostas FastAPI/Axios para string exibível no React
 * (evita passar arrays/objetos de validação 422 para `AlertModal` ou `{children}`).
 */
export const formatApiErrorDetail = (detail: unknown, fallback = 'Erro desconhecido'): string => {
  if (detail === null || detail === undefined) {
    return fallback;
  }
  if (typeof detail === 'string' && detail.trim()) {
    return translateKnownEnglishApiDetail(detail.trim());
  }
  if (Array.isArray(detail)) {
    const formatted = formatFastApiValidationErrors(detail).trim();
    return translateKnownEnglishApiDetail(formatted || fallback);
  }
  if (typeof detail === 'object') {
    const d = detail as Record<string, unknown>;
    if (typeof d.msg === 'string' && d.msg.trim()) {
      return translateKnownEnglishApiDetail(translatePydanticMsg(d.msg.trim()));
    }
    if (typeof d.message === 'string' && d.message.trim()) {
      return translateKnownEnglishApiDetail(d.message.trim());
    }
    try {
      return JSON.stringify(detail);
    } catch {
      return fallback;
    }
  }
  const s = String(detail).trim();
  return translateKnownEnglishApiDetail(s) || fallback;
};

type AxiosLikeError = {
  response?: {
    status?: number;
    data?: Record<string, unknown> | string | null;
  };
  message?: string;
  code?: string;
};

const extractResponsePayload = (error: AxiosLikeError): Record<string, unknown> | null => {
  const data = error.response?.data;
  if (data === null || data === undefined) {
    return null;
  }
  if (typeof data === 'object' && !Array.isArray(data)) {
    return data as Record<string, unknown>;
  }
  if (typeof data === 'string' && data.trim()) {
    return { detail: data.trim() };
  }
  return null;
};

const appendErrorRef = (message: string, payload: Record<string, unknown> | null): string => {
  const errorId = payload?.error_id;
  if (typeof errorId !== 'string' || !errorId.trim()) {
    return message;
  }
  if (message.toLowerCase().includes('ref:')) {
    return message;
  }
  return `${message} (ref: ${errorId.trim().toUpperCase()})`;
};

const httpStatusFallback = (status: number): string => {
  if (status === 401) {
    return 'Não autenticado. Faça login novamente.';
  }
  if (status === 403) {
    return 'Sem permissão para esta ação.';
  }
  if (status === 404) {
    return 'Recurso não encontrado.';
  }
  if (status === 422) {
    return 'Dados inválidos. Revise os campos e tente novamente.';
  }
  if (status >= 500) {
    return 'Erro interno no servidor. Tente novamente ou contacte o suporte.';
  }
  return `A requisição falhou (HTTP ${status}).`;
};

/**
 * Extrai a mensagem de erro de uma resposta da API para exibir direto na UI.
 *
 * Substitui o `err?.response?.data?.detail || 'fallback'` copiado pelas páginas:
 * aquele padrão jogava o `detail` cru no React, e num 422 do Pydantic o `detail`
 * é um array de objetos — o que renderiza `[object Object]` em vez do erro.
 */
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (error === null || error === undefined) {
    return fallback;
  }
  const err = error as AxiosLikeError;
  const status = err?.response?.status;
  const payload = extractResponsePayload(err);
  const detail = payload?.detail;

  if (detail !== undefined && detail !== null && detail !== '') {
    const formatted = formatApiErrorDetail(detail, fallback);
    return appendErrorRef(formatted, payload);
  }

  const message = typeof err?.message === 'string' ? err.message : '';

  if (status) {
    if (message.includes('status code')) {
      return translateKnownEnglishApiDetail(message);
    }
    return appendErrorRef(httpStatusFallback(status), payload);
  }

  if (err?.code === 'ERR_NETWORK' || /network error/i.test(message)) {
    return translateKnownEnglishApiDetail('Network Error');
  }

  if (message.trim()) {
    const translated = translateKnownEnglishApiDetail(message.trim());
    if (translated !== message.trim()) {
      return translated;
    }
  }

  return fallback;
};

const getErrorText = (error: any): string => {
  const detail = error?.response?.data?.detail;
  const responseData = error?.response?.data;
  const fallbackMessage = error?.message;

  if (Array.isArray(detail)) {
    const formatted = formatApiErrorDetail(detail, '');
    if (formatted) {
      return formatted;
    }
  }
  if (
    typeof detail === 'object' &&
    detail !== null &&
    'msg' in detail &&
    typeof (detail as { msg?: unknown }).msg === 'string'
  ) {
    const formatted = formatApiErrorDetail(detail, '');
    if (formatted) {
      return formatted;
    }
  }
  if (typeof detail === 'string' && detail.trim()) {
    return translateKnownEnglishApiDetail(detail.trim());
  }
  if (typeof detail?.message === 'string' && detail.message.trim()) {
    return translateKnownEnglishApiDetail(detail.message.trim());
  }
  if (typeof responseData?.message === 'string' && responseData.message.trim()) {
    return translateKnownEnglishApiDetail(responseData.message.trim());
  }
  if (typeof responseData === 'string' && responseData.trim()) {
    return translateKnownEnglishApiDetail(responseData.trim());
  }
  if (typeof fallbackMessage === 'string' && fallbackMessage.trim()) {
    return translateKnownEnglishApiDetail(fallbackMessage.trim());
  }
  return '';
};

const containsAny = (value: string, tokens: string[]): boolean => {
  const normalized = value.toLowerCase();
  return tokens.some((token) => normalized.includes(token));
};

export const isLikelyMigrationIssue = (error: any, endpointHints: string[] = []): boolean => {
  const status = error?.response?.status;
  if (status !== 500) {
    return false;
  }

  const rawUrl = String(error?.config?.url || '').toLowerCase();
  const endpointLooksRelated =
    endpointHints.length === 0 || endpointHints.some((hint) => rawUrl.includes(hint.toLowerCase()));

  if (!endpointLooksRelated) {
    return false;
  }

  const errorText = getErrorText(error);

  if (!errorText) {
    return true;
  }

  const schemaTokens = [
    'internal server error',
    'unknown column',
    "doesn't exist",
    'does not exist',
    'undefined column',
    'table',
    'sql',
    'operationalerror',
    'programmingerror',
  ];

  return containsAny(errorText, schemaTokens);
};

export const buildFriendlyApiError = (
  error: any,
  options: ApiErrorOptions
): { message: string; details: string } => {
  const extracted = getErrorText(error) || options.defaultMessage;
  const migrationIssue = isLikelyMigrationIssue(error, options.endpointHints || []);

  if (migrationIssue) {
    return {
      message: `${options.defaultMessage} ${MIGRATION_HINT_MESSAGE}`,
      details: extracted,
    };
  }

  return {
    message: options.defaultMessage,
    details: extracted,
  };
};
